import { Component, OnDestroy, OnInit } from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { DialogService } from "@progress/kendo-angular-dialog";
import { combineLatest, forkJoin, Observable } from "rxjs";
import { filter, first, map, mergeMap, switchMap } from "rxjs/operators";
import { I18nService } from "src/app/i18n/services";
import { DateUtility } from "src/app/interfaces/date";
import { NotificationFacade } from "src/app/notifications/services/notification.facade";
import { CirculationFacade } from "src/app/services/circulation.facade";
import { PersonFacade } from "src/app/services/person.facade";
import { StepFacade } from "src/app/step/services/step.facade";
import { SubSink } from "subsink";
import {
  AbstractUserService,
  IDefaultStepPayloadData,
  IPerson,
  Step
} from "../../../../interfaces";
import { LogClass, Logger, LogMethod } from "../../../../logger";
import { NavigationService } from "../../../../services";

@Component({
    selector: "circ-edit",
    templateUrl: "./edit.component.html",
    styleUrls: ["./edit.component.scss"],
    standalone: false
})
@LogClass({ source: "stepForConfirmation/EditComponent" })
export class EditComponent implements OnInit, OnDestroy {
  public minDueDate$: Observable<Date>;
  public maxDueDate$: Observable<Date>;
  public form: FormGroup;

  public loading = true;

  private logger: Logger;
  private subscriptions = new SubSink();

  constructor(
    private circulationFacade: CirculationFacade,
    private stepFacade: StepFacade,
    private userService: AbstractUserService,
    private navigate: NavigationService,
    private personFacade: PersonFacade,
    private dialogService: DialogService,
    private i18nService: I18nService,
    private notification: NotificationFacade
  ) {
    this.createForm();
  }

  public abort(): void {
    this.navigateToCurrentCirculation();
  }

  private navigateToCurrentCirculation(): void {
    this.circulationFacade
      .getCurrentCirculation()
      .pipe(
        map((circ) => circ.id),
        first()
      )
      .subscribe((circId) => this.navigate.toCirculation(circId));
  }

  public delete(): void {
    forkJoin({
      step_edit_deleteDlg_title: this.i18nService.get(
        "step_edit_deleteDlg_title"
      ),
      step_edit_deleteDlg_text: this.i18nService.get(
        "step_edit_deleteDlg_text"
      ),
      step_edit_deleteDlg_yes: this.i18nService.get("step_edit_deleteDlg_yes"),
      step_edit_deleteDlg_no: this.i18nService.get("step_edit_deleteDlg_no")
    })
      .pipe(
        mergeMap((translations) => {
          return this.dialogService.open({
            title: translations["step_edit_deleteDlg_title"],
            content: translations["step_edit_deleteDlg_text"],
            actions: [
              { text: translations["step_edit_deleteDlg_yes"], doDelete: true },
              { text: translations["step_edit_deleteDlg_no"], primary: true }
            ],
            width: 450,
            height: 200,
            minWidth: 250
          }).result;
        })
        // eslint-disable-next-line prettier/prettier
      )
      .subscribe((result) => {
        if (!(result as { doDelete: boolean }).doDelete) {
          return;
        }
        this.logger.info("deleting step");
        this.stepFacade
          .getCurrentStep()
          .pipe(
            first(),
            switchMap((step) => this.stepFacade.delete(step.props))
          )
          .subscribe((id) => {
            this.logger.info("deleted!");

            this.navigate.toCirculation(id);
          });
      });
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  @LogMethod()
  public ngOnInit(): void {
    const circulation$ = this.circulationFacade.getCurrentCirculation().pipe(
      filter((c) => !!c),
      first()
    );
    const step$ = this.stepFacade.getCurrentStep().pipe(
      filter((s) => !!s),
      first()
    );
    const dueDateRange$ = combineLatest([step$, circulation$]).pipe(
      switchMap(([step, circ]) =>
        circ.getValidDueDateRangeForStep(this.circulationFacade, step.id)
      ),
      map((range) => {
        const from =
          range.from < DateUtility.getTodayMin()
            ? DateUtility.getTodayMin()
            : range.from;
        const to = range.to;
        return { from, to };
      })
    );

    this.minDueDate$ = dueDateRange$.pipe(map((range) => range.from));
    this.maxDueDate$ = dueDateRange$.pipe(map((range) => range.to));

    this.subscriptions.sink = step$
      .pipe(
        mergeMap((step) => {
          const data = step.props.payload.data as IDefaultStepPayloadData;
          this.form.setValue({
            title: data?.title,
            comment: data?.comment || "",
            dueDate: step.props.dueDate,
            responsible: [],
            firstResponseApplies: step.props.tasks.firstResponseApplies,
            canAddDocuments: step.props.permissions?.canAddDocuments || false
          });
          return this.personFacade.getPersonByLogins(
            step.props.tasks.assignees.map((x) => x.user.login)
          );
        })
      )
      .subscribe((persons) => {
        this.form.patchValue({
          responsible: persons
        });
        this.loading = false;
      });
  }

  public save(): void {
    this.form.markAllAsTouched();
    if (!this.form.valid) {
      return;
    }

    this.logger.info("saving");
    this.loading = true;
    const stepToSave$ = this.stepFacade.getCurrentStep().pipe(
      first(),
      map(
        (step) =>
          new Step(
            {
              ...step.props,
              dueDate: DateUtility.middleOfDay(this.form.value.dueDate),
              payload: {
                ...step.props.payload,
                data: {
                  ...step.props.payload.data,
                  title: this.form.value.title,
                  comment: this.form.value.comment
                }
              },
              permissions: {
                ...(step.props.permissions || {}),
                canAddDocuments: this.form.value.canAddDocuments
              }
            },
            step.id
          )
      )
    );

    const users$ = this.userService.getUsers(
      this.form.value.responsible.map((x: IPerson) => x.login)
    );

    combineLatest([stepToSave$, users$])
      .pipe(
        first(),
        map(([step, users]) => {
          step.props.tasks = {
            assignees: users.map((u) => {
              return {
                id: 0,
                user: u
              };
            }),
            firstResponseApplies: this.form.value.firstResponseApplies
          };
          return step;
        }),
        switchMap((step) => this.stepFacade.save(step))
      )
      .subscribe({
        next: (ok) => {
          this.logger.info("saved:" + ok);

          this.navigateToCurrentCirculation();
        },
        error: (err) => {
          this.logger.info("error saving step: " + JSON.stringify(err));

          this.notification.showError(
            "Error while saving this step. Please ask your Administrator for help." +
              err.error.message
          );

          this.navigateToCurrentCirculation();
        }
      });
  }

  @LogMethod()
  private createForm() {
    this.form = new FormGroup({
      title: new FormControl("", [Validators.required]),
      responsible: new FormControl([], [Validators.required]),
      comment: new FormControl(""),
      dueDate: new FormControl(null, [Validators.required]),
      firstResponseApplies: new FormControl(false),
      canAddDocuments: new FormControl(false)
    });
  }
}
